import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShieldAlert } from "lucide-react";

import { useAuthenticate } from "../cacheQueries";
import type { Capability } from "../APIClient";
import { resolveCapabilityGate } from "../auth/session";

/**
 * Route guard: render the page only if the session holds `capability`.
 *
 * WHY THIS EXISTS SEPARATELY FROM THE NAV GATING
 *   Hiding a nav link hides a link. The routes in main.tsx were guarded on
 *   authentication alone, so before this, typing /configusers loaded the Users
 *   page for anybody with a session — the member list rendered (it comes from
 *   companydetails, which any member may read) and only the write actions
 *   failed. With one privileged role that was untidy; with operators it would
 *   be wrong, because "an operator cannot enter the Users page" is the entire
 *   definition of the role.
 *
 *   The server enforces every one of these capabilities independently. This
 *   guard exists so the product behaves the way it is described, not as the
 *   security boundary.
 *
 * THE DECISION IS NOT TWO-WAY
 *   "Allowed" and "denied" are not the only states. There is also "we do not
 *   know yet", and collapsing it into either of the others is a visible bug:
 *   treating unknown as allowed flashes the page to someone who may not be
 *   permitted, and treating unknown as denied bounces a legitimate admin to the
 *   dashboard on every cold load. So unknown renders nothing for a tick.
 *
 *   Reading the capability list off /authenticate rather than a query of its own
 *   is what keeps that simple: there is exactly one upstream request to be
 *   pending, so "do we know yet" has one answer instead of three that can
 *   disagree. The rules live in auth/session.ts (resolveCapabilityGate).
 *
 * A LOADED SESSION WINS OVER AN ERROR
 *   When a background refetch of /authenticate fails, React Query keeps the last
 *   good session and only flips `isError`. The guard decides on that session, so
 *   a transient API or database error does not unmount the page someone is
 *   working on.
 *
 * ON ERROR WITH NO SESSION AT ALL it shows "could not check" rather than
 * redirecting, so a failing API does not silently teleport someone away from
 * the URL they asked for. It is not a dead end: useAuthenticate retries the
 * request three times, then re-probes every 15 seconds while it keeps failing,
 * and the page appears as soon as an answer arrives. AppLayout shows the same
 * message for the cold-load case, where no routed page mounts until the session
 * has loaded.
 */
export function RequireCapability({
  capability,
  children,
  /**
   * Where to send a caller who is definitively not allowed. A redirect (rather
   * than an inline message) is right for a page they reached by typing a URL
   * they were never shown a link to.
   */
  redirectTo = "/",
}: {
  /**
   * The capability the page needs. A list means ANY of them, for a page made of
   * parts that each gate themselves (/configuration).
   */
  capability: Capability | readonly Capability[];
  children: React.ReactNode;
  redirectTo?: string;
}) {
  const { data, isError } = useAuthenticate();

  switch (resolveCapabilityGate({ data, isError }, capability)) {
    case "allow":
      return <>{children}</>;
    case "deny":
      return <Navigate to={redirectTo} replace />;
    case "error":
      return <PermissionCheckFailed />;
    case "signed-out":
      // A 401: main.tsx's own effect logs out and redirects to /login;
      // rendering nothing avoids racing it with a second navigation.
      return null;
    case "pending":
      return null;
  }
}

/** "Could not check your permissions", while /authenticate keeps failing. */
export function PermissionCheckFailed() {
  const { t } = useTranslation();
  return (
    // pt-24 / lg:ml-64 clear the fixed top bar and sidebar, as pages do.
    <div
      role="status"
      className="flex flex-col items-center justify-center gap-3 px-6 pb-12 pt-24 text-center font-primary lg:ml-64"
    >
      <ShieldAlert className="h-8 w-8 text-muted-foreground" />
      <p className="m-0 text-sm text-muted-foreground">
        {t(
          "permissions.checkFailed",
          "Could not check your permissions. Retrying…",
        )}
      </p>
    </div>
  );
}

export default RequireCapability;
